import React, { useState, useEffect, forwardRef } from "react";
import { cn } from "@/utils";

interface InnInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlurValidate?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Тип ИНН: физлицо (12 цифр) или юрлицо (10 цифр) */
  type?: "individual" | "company";
}

/**
 * Компонент ввода ИНН.
 * Физлицо: 12 цифр
 * Юрлицо: 10 цифр
 */
const InnInput = forwardRef<HTMLInputElement, InnInputProps>(
  ({ value, onChange, onBlurValidate, placeholder, className, disabled = false, type = "individual" }, ref) => {
    const [displayValue, setDisplayValue] = useState("");
    const [error, setError] = useState<string | null>(null);

    const maxLength = type === "individual" ? 12 : 10;
    const defaultPlaceholder = type === "individual" ? "000000000000" : "0000000000";

    useEffect(() => {
      setDisplayValue(value || "");
    }, [value]);

    const formatInn = (input: string): string => {
      // Только цифры
      const digits = input.replace(/\D/g, "");
      return digits.slice(0, maxLength);
    };

    const validateInn = (innStr: string): string | null => {
      if (!innStr || innStr.length < maxLength) {
        return null;
      }

      if (innStr.length !== maxLength) {
        return `ИНН должен содержать ${maxLength} цифр`;
      }

      // Проверка контрольных сумм
      const digits = innStr.split("").map(Number);

      if (type === "company") {
        // ИНН юрлица - 10 цифр, проверка 10-й цифры
        const weights = [2, 4, 10, 3, 5, 9, 4, 6, 8];
        const sum = weights.reduce((acc, w, i) => acc + w * digits[i], 0);
        const checkDigit = (sum % 11) % 10;
        if (checkDigit !== digits[9]) {
          return "Неверная контрольная сумма ИНН";
        }
      } else {
        // ИНН физлица - 12 цифр, проверка 11-й и 12-й цифр
        const weights11 = [7, 2, 4, 10, 3, 5, 9, 4, 6, 8];
        const weights12 = [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8];

        const sum11 = weights11.reduce((acc, w, i) => acc + w * digits[i], 0);
        const checkDigit11 = (sum11 % 11) % 10;

        const sum12 = weights12.reduce((acc, w, i) => acc + w * digits[i], 0);
        const checkDigit12 = (sum12 % 11) % 10;

        if (checkDigit11 !== digits[10] || checkDigit12 !== digits[11]) {
          return "Неверная контрольная сумма ИНН";
        }
      }

      return null;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      const formatted = formatInn(input);

      setDisplayValue(formatted);
      onChange(formatted);

      if (formatted.length === maxLength) {
        const validationError = validateInn(formatted);
        setError(validationError);
      } else {
        setError(null);
      }
    };

    const handleBlur = () => {
      if (displayValue && displayValue.length > 0 && displayValue.length < maxLength) {
        setError(`Введите ${maxLength} цифр ИНН`);
      } else if (displayValue.length === maxLength && !error && onBlurValidate) {
        onBlurValidate(displayValue);
      }
    };

    return (
      <div className="w-full">
        <input
          ref={ref}
          type="text"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder || defaultPlaceholder}
          disabled={disabled}
          maxLength={maxLength}
          className={cn(
            "block w-full px-3 py-2 border rounded-lg bg-surface-elevated text-fs-sm text-ink-1 placeholder:text-ink-4",
            "transition-colors focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30",
            error
              ? "border-red-400 focus:border-red-500 focus:ring-red-500/30 bg-red-50/50 dark:border-red-500 dark:bg-red-900/10"
              : "border-line",
            className,
          )}
        />
        {error && <p className="mt-1 text-xs text-error-600 dark:text-error-400">{error}</p>}
      </div>
    );
  },
);

InnInput.displayName = "InnInput";

export default InnInput;
